import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { manufacturingStages } from '../data/process';
import ProcessStage from './ProcessStage';
import StageVisualAnimation from './StageVisualAnimation';
import { ChevronLeft, ChevronRight, Play, Pause, RefreshCw } from 'lucide-react';

const ProcessSection = () => {
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [viewMode, setViewMode] = useState('animation'); // 'animation' or 'photo'
  const [progress, setProgress] = useState(0);

  const currentStage = manufacturingStages[activeStageIndex];

  // Automatic Step Progression Timer (5.5 seconds per step)
  useEffect(() => {
    let timer;
    let interval;

    if (isPlaying) {
      setProgress(0);
      const startTime = Date.now();
      const duration = 5500;

      interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const currentProgress = Math.min((elapsed / duration) * 100, 100);
        setProgress(currentProgress);
      }, 50);

      timer = setTimeout(() => {
        setActiveStageIndex((prev) => (prev < manufacturingStages.length - 1 ? prev + 1 : 0));
      }, duration);
    }

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [activeStageIndex, isPlaying]);

  const handleNext = () => {
    setActiveStageIndex((prev) => (prev < manufacturingStages.length - 1 ? prev + 1 : 0));
    setProgress(0);
  };

  const handlePrev = () => {
    setActiveStageIndex((prev) => (prev > 0 ? prev - 1 : manufacturingStages.length - 1));
    setProgress(0);
  };

  return (
    <section id="process-journey" style={{ padding: '100px 0', position: 'relative', background: '#ffffff' }}>
      <div className="container">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto 48px' }}
        >
          <span className="section-badge">
            <RefreshCw size={14} className="animate-spin-slow" />
            AUTOMATED MANUFACTURING WORKFLOW
          </span>
          <h2 className="section-title">
            FROM FABRIC TO <span className="gradient-text">GLOBAL MARKET</span>
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto 20px' }}>
            Watch the live simulated journey from raw denim roll cutting to international port dispatch.
          </p>

          {/* Auto-Play Controller & View Switcher Bar */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px', background: '#f8fafc', padding: '8px 18px', borderRadius: '99px', border: '1px solid var(--color-border)', boxShadow: '0 4px 16px rgba(15, 23, 42, 0.04)' }}>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: isPlaying ? 'rgba(224, 0, 135, 0.1)' : 'var(--text-main)',
                color: isPlaying ? 'var(--accent-magenta)' : '#ffffff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '99px',
                fontSize: '12px',
                fontWeight: 800,
                cursor: 'pointer'
              }}
            >
              {isPlaying ? <Pause size={14} /> : <Play size={14} />}
              <span>{isPlaying ? 'PAUSE AUTOMATION' : 'RESUME AUTOMATION'}</span>
            </button>

            <div style={{ width: '1px', height: '18px', background: 'var(--color-border)' }} />

            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setViewMode('animation')}
                style={{
                  background: viewMode === 'animation' ? 'linear-gradient(135deg, var(--accent-purple), var(--accent-magenta))' : 'transparent',
                  color: viewMode === 'animation' ? '#ffffff' : 'var(--text-subtle)',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '99px',
                  fontSize: '11px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                LIVE SIMULATION
              </button>
              <button
                onClick={() => setViewMode('photo')}
                style={{
                  background: viewMode === 'photo' ? 'linear-gradient(135deg, var(--accent-purple), var(--accent-magenta))' : 'transparent',
                  color: viewMode === 'photo' ? '#ffffff' : 'var(--text-subtle)',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '99px',
                  fontSize: '11px',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                PHOTO STAGE
              </button>
            </div>
          </div>
        </motion.div>

        {/* Top Animated Progress Countdown Bar */}
        <div style={{ width: '100%', height: '4px', background: '#f1f5f9', borderRadius: '2px', marginBottom: '32px', overflow: 'hidden', position: 'relative' }}>
          <motion.div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: 'linear-gradient(90deg, var(--accent-purple), var(--accent-magenta))',
              borderRadius: '2px',
              boxShadow: '0 0 10px var(--accent-magenta)'
            }}
          />
        </div>

        {/* Process Stage Navigation Pills */}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '16px',
            marginBottom: '40px',
            scrollbarWidth: 'thin',
            borderBottom: '1px solid var(--color-border)'
          }}
        >
          {manufacturingStages.map((stage, idx) => {
            const isActive = idx === activeStageIndex;
            return (
              <button
                key={stage.id}
                onClick={() => {
                  setActiveStageIndex(idx);
                  setProgress(0);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  background: isActive ? 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-magenta) 100%)' : '#f8fafc',
                  border: isActive ? 'none' : '1px solid var(--color-border)',
                  color: isActive ? '#ffffff' : 'var(--text-main)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: isActive ? 800 : 700,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  flexShrink: 0,
                  boxShadow: isActive ? '0 4px 16px var(--accent-glow)' : 'none'
                }}
              >
                <span style={{ color: isActive ? '#ffffff' : 'var(--accent-magenta)', fontWeight: 900 }}>
                  {stage.step}
                </span>
                <span>{stage.shortTitle}</span>
              </button>
            );
          })}
        </div>

        {/* Active Stage Display Panel (Animation or Photo) */}
        <AnimatePresence mode="wait">
          <motion.div
            key={`${currentStage.id}-${viewMode}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {viewMode === 'animation' ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: '40px',
                  alignItems: 'center',
                  background: '#ffffff',
                  border: '1px solid var(--color-border)',
                  boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
                  borderRadius: '16px',
                  padding: 'clamp(24px, 4vw, 48px)'
                }}
              >
                {/* Left: Dynamic Live Simulation Graphic */}
                <StageVisualAnimation stepId={currentStage.id} />

                {/* Right: Stage Details */}
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--accent-magenta)', letterSpacing: '0.1em', marginBottom: '12px' }}>
                    STAGE {currentStage.step} OF 08 • {currentStage.badge}
                  </div>
                  <h3 style={{ fontSize: 'clamp(26px, 3.5vw, 38px)', fontFamily: 'var(--font-heading)', fontWeight: 900, color: 'var(--text-main)', marginBottom: '12px', textTransform: 'uppercase' }}>
                    {currentStage.heading}
                  </h3>
                  <p style={{ fontSize: '16px', fontWeight: 700, color: 'var(--accent-purple)', marginBottom: '16px' }}>
                    "{currentStage.subheading}"
                  </p>
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', lineHeight: '1.6', marginBottom: '24px' }}>
                    {currentStage.description}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {currentStage.details.map((detail, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-main)', fontWeight: 700 }}>
                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-magenta)' }} />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <ProcessStage stage={currentStage} />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Stepper Navigation Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '36px' }}>
          <button
            onClick={handlePrev}
            className="btn-secondary"
            style={{ padding: '12px 24px', fontSize: '13px' }}
          >
            <ChevronLeft size={16} />
            <span>PREVIOUS STAGE</span>
          </button>

          <div style={{ fontSize: '13px', color: 'var(--text-subtle)', fontWeight: 800, letterSpacing: '0.05em' }}>
            STAGE {activeStageIndex + 1} OF {manufacturingStages.length}
          </div>

          <button
            onClick={handleNext}
            className="btn-secondary"
            style={{ padding: '12px 24px', fontSize: '13px' }}
          >
            <span>NEXT STAGE</span>
            <ChevronRight size={16} />
          </button>
        </div>

      </div>
    </section>
  );
};

export default ProcessSection;
